/**
 * 人群卡片组件
 */

import React from 'react';
import { Card, Space, Tag, Checkbox, Typography } from 'antd';
import type { TargetAudience, ScriptType } from '../../types';

const { Text, Paragraph } = Typography;

// 剧本类型标签
const ScriptTag: React.FC<{ type: ScriptType }> = ({ type }) => {
    const config = {
        blue: { color: 'blue', label: '🔵 理性' },
        green: { color: 'green', label: '🟢 感性' },
        hybrid: { color: 'purple', label: '⚪ 混合' },
    };
    const { color, label } = config[type];
    return <Tag color={color}>{label}</Tag>;
};

interface AudienceCardProps {
    audience: TargetAudience;
    selected: boolean;
    onToggle: () => void;
}

export const AudienceCard: React.FC<AudienceCardProps> = ({ audience, selected, onToggle }) => {
    // 兼容处理
    const scriptType = audience.recommended_script || audience.copywriting_suggestions?.recommended_script || 'hybrid';

    return (
        <Card
            size="small"
            hoverable
            onClick={onToggle}
            style={{
                borderColor: selected ? '#1890ff' : undefined,
                backgroundColor: selected ? '#e6f7ff' : undefined,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Checkbox checked={selected} style={{ marginRight: 8 }} />
                <div style={{ flex: 1 }}>
                    {/* 头部 */}
                    <div style={{ marginBottom: 8 }}>
                        <Space>
                            <Text strong>{audience.name}</Text>
                            <Tag color={audience.category === '通用' ? 'gold' : 'cyan'}>
                                {audience.category}
                            </Tag>
                            <ScriptTag type={scriptType} />
                        </Space>
                    </div>

                    {/* 描述 */}
                    <Paragraph
                        type="secondary"
                        ellipsis={{ rows: 2, expandable: true }}
                        style={{ marginBottom: 8 }}
                    >
                        {audience.description}
                    </Paragraph>

                    {/* 人口统计简要 */}
                    {audience.demographics && (
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                {audience.demographics.age_range} · {audience.demographics.gender_tendency} · {audience.demographics.income_level}收入
                            </Text>
                        </div>
                    )}

                    {/* 痛点 */}
                    {audience.pain_points && audience.pain_points.length > 0 && (
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>痛点: </Text>
                            {audience.pain_points.slice(0, 3).map((point, i) => (
                                <Tag key={i} color="red" style={{ fontSize: 11 }}>{point}</Tag>
                            ))}
                        </div>
                    )}

                    {/* 核心诉求 */}
                    {audience.desires && audience.desires.length > 0 && (
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>诉求: </Text>
                            {audience.desires.slice(0, 2).map((desire, i) => (
                                <Tag key={i} color="green" style={{ fontSize: 11 }}>{desire}</Tag>
                            ))}
                        </div>
                    )}

                    {/* 购买动机 */}
                    {audience.purchase_motivation && (
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>购买动机: </Text>
                            <Text style={{ fontSize: 12 }}>{audience.purchase_motivation}</Text>
                        </div>
                    )}

                    {/* 文案建议亮点 */}
                    {audience.copywriting_suggestions && (
                        <div style={{ marginTop: 8, padding: 8, background: '#f6ffed', borderRadius: 4 }}>
                            <Text style={{ fontSize: 11, color: '#52c41a' }}>💡 文案建议: </Text>
                            <Text style={{ fontSize: 11 }}>
                                {audience.copywriting_suggestions.tone_of_voice}风格 · {audience.copywriting_suggestions.headline_angle}
                            </Text>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default AudienceCard;
